import { useQuery } from "@tanstack/react-query";
import type { FeatureCollection, Point } from "geojson";
import { type ComponentType, useMemo } from "react";

import { api } from "../../api/client";
import { t } from "../../i18n/i18n";
import { useUi } from "../../state/ui";
import { revealPaint, revealStyle, useMapRevealed } from "../reveal";
import { useDeployment } from "../units/useDeployment";

/** Réseau de manœuvre du fuseau (bouton « routes » de la carte iPad) : rocades R11 et R13, pénétrantes P10 et
 *  P12, calculées par le serveur sur le réseau routier du terrain — les mêmes que le §3.f, le §4 et l'annexe C
 *  (C6, carte C7) de l'ordre. Couleurs de l'iPad (14/10/2026) : rocades en vert, pénétrantes en rouge, trait sur
 *  un liseré blanc qui les distingue des limites du fuseau ; étiquette à mi-longueur du tracé. */

// `MapLibreMapView.roadGreen` et `roadRed` de l'iPad, comme la page C7 (`logistics_map.py`).
export const ROCADE_GREEN = "#176617";
export const PENETRANTE_RED = "#C13C3C";
export const roadColour = (kind: string | undefined) => (kind === "rocade" ? ROCADE_GREEN : PENETRANTE_RED);

interface EngineParts {
  Source: ComponentType<any>;
  Layer: ComponentType<any>;
  Marker: ComponentType<any>;
}

const EMPTY: FeatureCollection = { type: "FeatureCollection", features: [] };

export function useTacticalRoads(enabled: boolean) {
  const exerciseId = useUi((s) => s.exerciseId);
  const opo = useUi((s) => s.opo);
  const { data: deployment } = useDeployment();
  const isCentac = deployment?.level === "CENTAC";
  const version = deployment?.exercise_version ?? null;
  return useQuery({
    queryKey: ["tactical-roads", exerciseId, isCentac ? opo : 1, version],
    queryFn: () => api.tacticalRoads(exerciseId as string, isCentac ? opo : 1),
    enabled: enabled && Boolean(exerciseId) && Boolean(deployment?.corridor),
    staleTime: Infinity,
    placeholderData: (prev) => prev,
  });
}

export function TacticalRoadsLayers({ Source, Layer, Marker }: EngineParts) {
  const visible = useUi((s) => s.controlVisible.roads === true);
  const revealed = useMapRevealed();
  const { data } = useTacticalRoads(visible);
  const lines = useMemo(
    () => ({ ...EMPTY, features: (data?.features ?? []).filter((f) => f.geometry.type === "LineString") }),
    [data],
  );
  const labels = useMemo(
    () =>
      (data?.features ?? []).filter((f) => f.geometry.type === "Point") as {
        geometry: Point;
        properties: { label: string; kind: string } | null;
      }[],
    [data],
  );
  if (!visible || !data) return null;
  return (
    <>
      <Source id="gx-tactical-roads" type="geojson" data={lines}>
        {/* Liseré blanc, puis le trait de la couleur de l'iPad ; pénétrantes au-dessus des rocades qu'elles coupent. */}
        <Layer
          id="tactical-roads-casing"
          type="line"
          layout={{ "line-cap": "round", "line-join": "round" }}
          paint={revealPaint("line", { "line-color": "#ffffff", "line-width": 6 }, revealed)}
        />
        <Layer
          id="tactical-roads"
          type="line"
          layout={{ "line-cap": "round", "line-join": "round", "line-sort-key": ["case", ["==", ["get", "kind"], "rocade"], 0, 1] }}
          paint={revealPaint(
            "line",
            { "line-color": ["case", ["==", ["get", "kind"], "rocade"], ROCADE_GREEN, PENETRANTE_RED], "line-width": 3 },
            revealed,
          )}
        />
      </Source>
      {labels.map((label) => (
        <Marker
          key={label.properties?.label}
          longitude={label.geometry.coordinates[0]}
          latitude={label.geometry.coordinates[1]}
          anchor="center"
          style={{ pointerEvents: "none" }}
        >
          <div style={revealStyle(revealed)}>
            <div
              title={label.properties?.kind === "rocade" ? t("Rocade") : t("Pénétrante")}
              className="pointer-events-none flex h-5 items-center rounded-md border border-line-strong bg-surface px-1.5 shadow-[0_1px_2px_rgb(16_18_24/0.12)]"
            >
              <span className="font-mono text-[12px] leading-none font-bold" style={{ color: roadColour(label.properties?.kind) }}>
                {label.properties?.label}
              </span>
            </div>
          </div>
        </Marker>
      ))}
    </>
  );
}
